// @ts-nocheck
/*
 * Port of views/dashboard/numbers/_form.js (origin/main-dev, 524 lines).
 * BYTE-IDENTICAL — Individual vs File format branch, DID/VMN/TOLL FREE
 * type-conditional fields (state code for DID, linked DID picker for
 * VMN/TOLL FREE, category for DID), auto-currency (IN→INR, else→USD),
 * cost=0 lock when category is Free, template download for file upload
 * with number-type-specific columns. PrimeReact Button in template-
 * download row → shadcn Button.
 */
import React, { useState, useEffect } from 'react';
/* Local micro-shim for react-bootstrap Form.* — renders native form-control
 * markup so the file no longer needs the compat shim while the JSX below
 * stays byte-identical. */
const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>{children}</div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>{children}</label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select') return <select className={cls} {...props}>{children}</select>;
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, ...props }: any) => (
  <select className={_cx('form-control form-select', className)} {...props}>{children}</select>
);
Form.Check = ({ type = 'checkbox', label, id, className, inline, children, ...props }: any) => (
  <div className={_cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && <label htmlFor={id} className="form-check-label">{label}</label>}
    {children}
  </div>
);
Form.Text = ({ muted, className, children, ...props }: any) => (
  <small className={_cx('form-text', muted && 'text-muted', className)} {...props}>{children}</small>
);
import downloadTemplate from '@/utilities/downloadCsv';
import {
  API_COUNTRY_CODES_LIST,
  API_STATES_LIST,
  API_OPERATORS_LIST,
  API_TRUNKS_LIST,
  API_AVAILABLE_DID_NUMBERS,
} from '@/service/url';
import { getApiCall, postApiCall } from '@/service/httpCall';
import {
  NUMBERS_FORMAT_TYPES,
  NUMBERS_FORMAT_FILE,
  NUMBERS_FORMAT_INDIVIDUAL,
  NUMBERS_TYPES,
  UPLOAD_NUMBER_DID,
  UPLOAD_NUMBER_OTHERS,
  SAMPLE_DATA_DID,
  SAMPLE_DATA_OTHERS,
  NUMBER_ROUTING_TYPE,
  NUMBER_COST_MIN,
  NUMBER_COST_MAX,
} from '@/utilities/constant';
import { Button } from '@/components/ui/button';
import {
  User as UserIcon,
  UploadCloud,
  Phone as PhoneIcon,
  PhoneForwarded,
  PhoneIncoming,
  ArrowRightLeft,
  Gift,
  DollarSign,
} from 'lucide-react';

/**
 * Card-style radio option — replaces the small <select> dropdowns for
 * mutually-exclusive choices with 2–3 options (Format Type, Number Type,
 * Number Routing, Category). Same visual grammar as the Campaigns
 * wizard: native `<input type="radio">` on the left for the radio
 * indicator, tinted icon well, bold label + one-line description.
 */
function RadioCard({ name, value, checked, onChange, disabled, icon, label, description }: any) {
  return (
    <label
      className={
        'group flex flex-1 min-w-[160px] cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ' +
        (disabled ? 'cursor-not-allowed opacity-60 ' : '') +
        (checked
          ? 'border-primary bg-primary/[0.08] text-primary'
          : 'border-border bg-card text-foreground hover:border-primary/40 hover:bg-primary/[0.04]')
      }
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="mt-0.5 h-4 w-4 shrink-0 accent-current"
      />
      {icon && (
        <span
          className={
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-md ' +
            (checked ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground')
          }
        >
          {icon}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold">{label}</span>
        {description && (
          <span className="mt-0.5 block text-xs text-muted-foreground">{description}</span>
        )}
      </span>
    </label>
  );
}


const getTrunkAllowedCountries = (trunk) => {
  if (!trunk) return [];
  const mapped = (trunk.country_codes || []).map((c) => String(c).toUpperCase());
  if (mapped.length) return mapped;
  return Number(trunk.is_international) === 1 ? [] : ['IN'];
};

function changeUploadFile(props, e) {
  const file = e.target.files[0];
  if (file && file.type === 'text/csv') {
    props.setUploadFile(file);
    props.setIsFileUpload(true);
  } else {
    props.setToastProps({ message: 'Please upload a CSV file.', type: 'danger' });
    props.setUploadFile(null);
    props.setIsFileUpload(false);
  }
}

const getColumnsAndFilename = (numberType) => {
  let columns;
  let filename;
  let sample_data;

  if (numberType === 'DID') {
    columns = UPLOAD_NUMBER_DID;
    filename = `${numberType}-Upload.csv`;
    sample_data = SAMPLE_DATA_DID;
  } else if (numberType === 'VMN') {
    columns = UPLOAD_NUMBER_OTHERS;
    filename = `${numberType}-Upload.csv`;
    sample_data = SAMPLE_DATA_OTHERS;
  } else {
    columns = UPLOAD_NUMBER_OTHERS;
    filename = `${numberType}-Upload.csv`;
    sample_data = SAMPLE_DATA_OTHERS;
  }

  return { columns, filename, sample_data };
};

function RenderFormatTypeFields(props) {
  props = props['props'];

  const isFree = props.category === 'Free';
  const isIndia = props.countryCodeId === 'IN';
  const currencySymbol = isIndia ? '₹' : '$';
  const callingCodePrefix = props.countryCallingCode || '';

  if (props.formatType === NUMBERS_FORMAT_INDIVIDUAL) {
    return (
      <>
        <Form.Group className="col-md-3 form-group mb-1">
          <Form.Label htmlFor="">
            {props.numberType ? props.numberType : 'Number Type'}:{' '}
            <span style={{ color: 'red', margin: '0 4px' }}>*</span>
          </Form.Label>
          <div className="input-group">
            {callingCodePrefix ? <span className="input-group-text">+{callingCodePrefix}</span> : null}
            <Form.Control
              type="text"
              placeholder={props.numberType ? props.numberType : 'Number Type'}
              id="did"
              value={props.did}
              maxLength={10}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                props.setDid(value);
              }}
              required
            />
          </div>
        </Form.Group>

        <Form.Group className="col-md-3 form-group mb-0">
          <Form.Label htmlFor="">
            Cost: <span style={{ color: 'red', margin: '0 4px' }}>*</span>
          </Form.Label>
          {isFree ? (
            <Form.Control
              type="text"
              placeholder="Cost"
              id="cost"
              value="0"
              readOnly
              disabled
              className="bg-light"
              style={{ cursor: 'not-allowed' }}
              required
            />
          ) : (
            <Form.Control
              type="number"
              placeholder="Cost"
              id="cost"
              min={NUMBER_COST_MIN}
              max={NUMBER_COST_MAX}
              value={props.cost}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  props.setCost(value);
                } else if (Number(value) > NUMBER_COST_MAX) {
                  props.setCost(NUMBER_COST_MAX);
                } else if (Number(value) >= NUMBER_COST_MIN) {
                  props.setCost(value);
                } else {
                  props.setCost(NUMBER_COST_MIN);
                }
              }}
              required
            />
          )}
        </Form.Group>

        {(props.numberType === 'VMN' || props.numberType === 'TOLL FREE') && (
          <Form.Group className="col-md-3 form-group mb-0">
            <Form.Label htmlFor="">
              DID Number: <span style={{ color: 'red', margin: '0 4px' }}>*</span>
            </Form.Label>
            <Form.Select
              id="linkedDidId"
              value={props.linkedDidId || ''}
              className="p-2"
              onChange={(e) => props.setLinkedDidId(e.target.value)}
              required
            >
              <option value="">Select DID Number</option>
              {(props.availableDidList || []).map(function (val, idx) {
                return (
                  <option key={idx} value={val.id}>
                    {val.actual_number}
                  </option>
                );
              })}
            </Form.Select>
            {(props.availableDidList || []).length === 0 && (
              <small className="text-danger">No available DID numbers found.</small>
            )}
          </Form.Group>
        )}
      </>
    );
  } else if (props.formatType === NUMBERS_FORMAT_FILE) {
    return (
      <>
        <Form.Group className="col-md-8 form-group mb-1">
          <Form.Label htmlFor="">
            File: <span style={{ color: 'red', margin: '0 4px' }}>*</span>
          </Form.Label>
          <Form.Control
            type="file"
            placeholder="Bulk Upload Numbers"
            id="uploadNumbers"
            onChange={(e) => changeUploadFile(props, e)}
            accept=".csv"
            required
          />
        </Form.Group>
        <Form.Group className="col-md-4 form-group mb-1 d-flex align-items-end">
          {props.numberType && (
            <Button
              onClick={(event) => {
                const { columns, filename, sample_data } = getColumnsAndFilename(props.numberType);
                downloadTemplate(event, columns, filename, null, sample_data);
              }}
            >
              Download Template
            </Button>
          )}
        </Form.Group>
      </>
    );
  }
  return <></>;
}

export default function RenderNumberForm(props) {
  const [countryCodesList, setCountryCodesList] = useState([]);
  const [stateCodeList, setStateCode] = useState([]);
  const [operatorsList, setOperatorsList] = useState([]);
  const [trunksList, setTrunksList] = useState([]);
  const [availableDidList, setAvailableDidList] = useState([]);

  useEffect(() => {
    getApiCall(API_COUNTRY_CODES_LIST, {}).then(onCountryCodesListSuccess).catch(onError);
    getApiCall(API_OPERATORS_LIST, {}).then(onOperatorListSuccess).catch(onError);
    getApiCall(API_TRUNKS_LIST, {}).then(onTrunksListSuccess).catch(onError);
  }, []);

  function onOperatorListSuccess(resp) {
    setOperatorsList(resp);
  }

  function onTrunksListSuccess(resp) {
    setTrunksList(resp);
  }

  useEffect(() => {
    if (props.numberType === 'VMN' || props.numberType === 'TOLL FREE') {
      getApiCall(API_AVAILABLE_DID_NUMBERS, {})
        .then((resp) => setAvailableDidList(Array.isArray(resp) ? resp : []))
        .catch((err) => {
          console.log(err);
          setAvailableDidList([]);
        });
    } else {
      setAvailableDidList([]);
      if (props.setLinkedDidId) {
        props.setLinkedDidId('');
      }
    }
  }, [props.numberType]);

  useEffect(() => {
    if (props.countryCodeId) {
      const params = { countryCode: props.countryCodeId };
      postApiCall(API_STATES_LIST, params)
        .then((response) => setStateCode(response))
        .catch((error) => console.error('Error while fetching states:', error));

      if (props.countryCodeId === 'IN') {
        props.setCurrency('INR');
      } else if (props.countryCodeId) {
        props.setCurrency('USD');
      }
    }
  }, [props.countryCodeId]);

  useEffect(() => {
    if (props.formatType === NUMBERS_FORMAT_FILE && !props.countryCodeId && !props.currency) {
      props.setCurrency('USD');
    }
  }, [props.formatType]);

  function onCountryCodesListSuccess(resp) {
    setCountryCodesList(resp);
  }

  function onError(err) {
    console.log(err);
  }

  
  const isDidIndividual = props.numberType === 'DID' && props.formatType === NUMBERS_FORMAT_INDIVIDUAL;
  const selectedTrunk = trunksList.find((t) => String(t.id) === String(props.trunks));
  const trunkCountryCodes = getTrunkAllowedCountries(selectedTrunk);
 
  const countryOptions =
    isDidIndividual && trunkCountryCodes.length
      ? countryCodesList.filter((c) => trunkCountryCodes.includes(String(c.code).toUpperCase()))
      : countryCodesList;
  const countryLocked = isDidIndividual && !props.trunks;


  const operatorOptions =
    isDidIndividual && selectedTrunk
      ? operatorsList.filter((o) => Number(o.is_international) === Number(selectedTrunk.is_international))
      : operatorsList;
  const operatorLocked = isDidIndividual && !props.trunks;

  function handleTrunkChange(trunkId) {
    props.setTrunks(trunkId);

    if (!trunkId) {
      props.setCountryCodeId('');
      props.setStateCode('');
      props.setCurrency('');
      props.setOperator('');
      return;
    }

    const trunk = trunksList.find((t) => String(t.id) === String(trunkId));

    const trunkType = Number(trunk?.is_international) === 1 ? 1 : 0;
    const currentOperator = operatorsList.find((o) => String(o.name) === String(props.operator));
    if (currentOperator && Number(currentOperator.is_international) !== trunkType) {
      props.setOperator('');
    }

    const codes = getTrunkAllowedCountries(trunk);
    if (codes.length === 0) return;

    const current = String(props.countryCodeId || '').toUpperCase();
    if (codes.includes(current)) return;

    props.setStateCode('');
    if (codes.length === 1) {
      props.setCountryCodeId(codes[0]);
    } else {
      props.setCountryCodeId('');
      props.setCurrency('');
    }
  }

  const selectedCountry = countryCodesList.find((country) => country.code === props.countryCodeId);
  const countryCallingCode = String(selectedCountry?.calling_code || '').trim().replace(/^\+/, '');

  return (
    <>
      {/* Format Type card selector — Individual vs File. */}
      <div className="mb-4">
        <label className="mb-1.5 block text-sm font-semibold text-foreground/80">
          Format Type <span className="text-destructive">*</span>
        </label>
        <div className="flex flex-wrap gap-2">
          <RadioCard
            name="formatType"
            value={NUMBERS_FORMAT_INDIVIDUAL}
            checked={props.formatType === NUMBERS_FORMAT_INDIVIDUAL}
            onChange={() => props.setFormatType(NUMBERS_FORMAT_INDIVIDUAL)}
            icon={<UserIcon className="h-4 w-4" />}
            label="Individual"
            description="Enter a single number by hand."
          />
          <RadioCard
            name="formatType"
            value={NUMBERS_FORMAT_FILE}
            checked={props.formatType === NUMBERS_FORMAT_FILE}
            onChange={() => props.setFormatType(NUMBERS_FORMAT_FILE)}
            icon={<UploadCloud className="h-4 w-4" />}
            label="File"
            description="Upload a CSV to bulk-add numbers."
          />
        </div>
      </div>

      {/* Number Type card selector — DID / VMN / TOLL FREE. */}
      <div className="mb-4">
        <label className="mb-1.5 block text-sm font-semibold text-foreground/80">
          Number Type <span className="text-destructive">*</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {NUMBERS_TYPES.map((val: any) => {
            const iconMap: Record<string, any> = {
              DID: <PhoneIcon className="h-4 w-4" />,
              VMN: <PhoneForwarded className="h-4 w-4" />,
              'TOLL FREE': <PhoneIncoming className="h-4 w-4" />,
            };
            return (
              <RadioCard
                key={val.value}
                name="numberType"
                value={val.value}
                checked={props.numberType === val.value}
                onChange={() => props.setNumberType(val.value)}
                icon={iconMap[val.value]}
                label={val.label}
              />
            );
          })}
        </div>
      </div>

      {/* Number Routing card selector — only shown for Individual. */}
      {props.formatType === NUMBERS_FORMAT_INDIVIDUAL && (
        <div className="mb-4">
          <label className="mb-1.5 block text-sm font-semibold text-foreground/80">
            Number Routing
          </label>
          <div className="flex flex-wrap gap-2">
            {NUMBER_ROUTING_TYPE.map((rt: any) => {
              const iconMap: Record<string, any> = {
                incoming: <PhoneIncoming className="h-4 w-4" />,
                outgoing: <PhoneForwarded className="h-4 w-4" />,
                both: <ArrowRightLeft className="h-4 w-4" />,
              };
              return (
                <RadioCard
                  key={rt.value}
                  name="numberRoutingType"
                  value={rt.value}
                  checked={props.numberRoutingType === rt.value}
                  onChange={() => props.setNumberRoutingType(rt.value)}
                  icon={iconMap[rt.value]}
                  label={rt.label}
                />
              );
            })}
          </div>
        </div>
      )}

      <div className="row g-4">

        {props.numberType === 'DID' && props.formatType === NUMBERS_FORMAT_INDIVIDUAL && (
          <Form.Group className="col-md-3 form-group mb-1">
            <Form.Label htmlFor="">
              Trunks: <span style={{ color: 'red', margin: '0 4px' }}>*</span>
            </Form.Label>
            <Form.Select
              id="trunks"
              value={props.trunks}
              className="p-2"
              onChange={(e) => handleTrunkChange(e.target.value)}
              required
            >
              <option value="">Select Trunks</option>
              {trunksList.map(function (val, idx) {
                return (
                  <option key={idx} value={val.id}>
                    {val.name}
                  </option>
                );
              })}
            </Form.Select>
            {selectedTrunk && (
              <small className="text-muted">
                {Number(selectedTrunk.is_international) === 1 ? 'International trunk' : 'Domestic trunk'}
              </small>
            )}
          </Form.Group>
        )}

        {props.formatType === NUMBERS_FORMAT_INDIVIDUAL && (
          <Form.Group className="col-md-3 form-group mb-1">
            <Form.Label htmlFor="">
              Country: <span style={{ color: 'red', margin: '0 4px' }}>*</span>
            </Form.Label>
            <Form.Select
              id="countryCodeId"
              required
              value={props.countryCodeId}
              className="p-2"
              disabled={countryLocked}
              onChange={(e) => props.setCountryCodeId(e.target.value)}
            >
              <option value={''}>{countryLocked ? 'Select trunk first' : 'Select Country Code'}</option>
              {countryOptions.map(function (val, idx) {
                return (
                  <option key={idx} value={val.code}>
                    {val.name}
                    {` (${val.code})`}
                  </option>
                );
              })}
            </Form.Select>
          </Form.Group>
        )}

        {props.formatType === NUMBERS_FORMAT_INDIVIDUAL && (
          <Form.Group className="col-md-2 form-group mb-1">
            <Form.Label htmlFor="callingCode">Calling Code:</Form.Label>
            <Form.Control
              id="callingCode"
              type="text"
              className="p-2"
              value={countryCallingCode ? `+${countryCallingCode}` : ''}
              placeholder="Select country"
              readOnly
              disabled
              style={{ cursor: 'not-allowed', backgroundColor: '#e9ecef' }}
            />
          </Form.Group>
        )}

        {props.numberType === 'DID' && props.formatType === NUMBERS_FORMAT_INDIVIDUAL && (
          <Form.Group className="col-md-4 form-group mb-1">
            <Form.Label htmlFor="">
              State: <span style={{ color: 'red', margin: '0 4px' }}>*</span>
            </Form.Label>
            <Form.Select
              id="stateCode"
              required
              value={props.stateCode}
              className="p-2"
              onChange={(e) => props.setStateCode(e.target.value)}
            >
              <option value={''}>Select State Code</option>
              {stateCodeList &&
                stateCodeList.map(function (val, idx) {
                  return (
                    <option key={idx} value={val.isoCode}>
                      {val.name}
                      {` (${val.isoCode})`}
                    </option>
                  );
                })}
            </Form.Select>
          </Form.Group>
        )}
        {props.numberType === 'DID' && (
          <div className="col-md-6 form-group mb-1">
            <label className="form-label">
              Category <span className="text-destructive">*</span>
            </label>
            <div className="flex flex-wrap gap-2">
              <RadioCard
                name="category"
                value="Free"
                checked={props.category === 'Free'}
                onChange={() => props.setCategory('Free')}
                icon={<Gift className="h-4 w-4" />}
                label="Free"
                description="Cost is locked to 0."
              />
              <RadioCard
                name="category"
                value="Paid"
                checked={props.category === 'Paid'}
                onChange={() => props.setCategory('Paid')}
                icon={<DollarSign className="h-4 w-4" />}
                label="Paid"
                description="Enter a per-number cost."
              />
            </div>
          </div>
        )}
        {(props.numberType === 'DID' || props.numberType === 'VMN' || props.numberType === 'TOLL FREE') && (
          <Form.Group className="col-md-2 form-group mb-1">
            <Form.Label htmlFor="">
              Currency: <span style={{ color: 'red', margin: '0 4px' }}>*</span>
            </Form.Label>
            <Form.Select
              id="currency"
              value={props.formatType === NUMBERS_FORMAT_FILE ? props.currency || 'USD' : props.currency}
              className="p-2"
              onChange={(e) => props.setCurrency(e.target.value)}
              required
              disabled={props.countryCodeId || props.formatType === NUMBERS_FORMAT_FILE ? true : false}
              style={
                props.countryCodeId || props.formatType === NUMBERS_FORMAT_FILE
                  ? { cursor: 'not-allowed', backgroundColor: '#e9ecef' }
                  : {}
              }
            >
              <option value="">Select Currency</option>
              <option value="INR">INR</option>
              <option value="USD">USD</option>
            </Form.Select>
          </Form.Group>
        )}
        {props.formatType === NUMBERS_FORMAT_INDIVIDUAL && (
          <Form.Group className="col-md-3 form-group mb-1">
            <Form.Label htmlFor="">
              Operator: <span style={{ color: 'red', margin: '0 4px' }}>*</span>
            </Form.Label>
            <Form.Select
              id="operator"
              placeholder="Operator"
              className="p-2"
              required
              disabled={operatorLocked}
              value={props.operator}
              onChange={(e) => props.setOperator(e.target.value)}
            >
              <option value={''}>{operatorLocked ? 'Select trunk first' : 'Select Operator'}</option>
              {operatorOptions.map(function (val, idx) {
                return (
                  <option key={idx} value={val.name}>
                    {val.name}
                  </option>
                );
              })}
            </Form.Select>
          </Form.Group>
        )}

        <RenderFormatTypeFields props={{ ...props, countryCallingCode, availableDidList }} />
      </div>
      {props.formatType == NUMBERS_FORMAT_FILE && (
        <div
          style={{
            background: '#FFF8C6',
            color: '#6B4E16',
            padding: '14px 16px',
            borderRadius: '8px',
            border: '1px solid #FFE08A',
            fontSize: '14px',
            lineHeight: '1.6',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: '6px' }}>⚠️ Important Information</div>
          <ul style={{ margin: 0, paddingLeft: '18px' }}>
            {props.numberType === 'DID' && (
              <li>
                If the <b>Category</b> is set to <b>Free</b>, the <b>Cost</b> value in the template must be
                <b> 0 (Zero)</b>.
              </li>
            )}

            {props.numberType === 'DID' && (
              <li>
                <b>TrunkId</b> is required for each row. Provide a valid Trunk Id per number in the template.
              </li>
            )}

            <li>
              Set <b>Number Routing</b> in the template using <b>incoming</b>, <b>outgoing</b>, or <b>both</b>.
            </li>

            <li>
              To correctly fill the template, refer to the dropdown values for <b>Country Code</b> and{' '}
              <b>State Code</b> by selecting <b>Format Type</b> as <b>Individual</b>.
            </li>

            <li>
              <b>Calling Code</b> is set automatically from each row's <b>Country Code</b> (e.g. IN → +91) and
              can't be entered in the template.
            </li>
          </ul>
        </div>
      )}
      <hr />
    </>
  );
}
